import React, { useEffect } from "react";
import Box from "@/ui/atoms/Box";
import { type BoxProps } from "@/ui/atoms/Box";
import OverviewTopBar from "@/ui/organisms/DealOverview/OverviewTopBar";
import type { DealPropertyInfoWithId } from "@/ui/organisms/dealTypes";
import Loader from "@/ui/atoms/Loader";
import LayoutFillParentCentered from "@/ui/layouts/LayoutFillParentCentered";
// documents view moved to its own factory
import {
    Button,
    Dropdown,
    DropdownContent,
    DropdownSelectItem,
    DropdownSelect,
    Icon,
    IconBox,
} from "@proda-ai/ui-kit";
import LayoutWithTopBar from "@/ui/layouts/LayoutWithTopBar";
import type { TDealsOverviewTab } from "@/ui/router/routerPaths";
import MessageWithButton from "@/ui/molecules/MessageWithButton";

// formatted date handled inside documents view
import { useMUIMediaQuery as useMediaQuery } from "@proda-ai/ui-kit";
import VerticalDivider from "@/ui/molecules/VerticalDivider/VerticalDivider";

const ERROR_RETRIEVING_DEAL =
    "There was an error retrieving the project. Please try again.";

export const testIds = {
    activityButton: "ViewDealOverview-activityButton",
    detailButton: "ViewDealOverview-detailButton",
    tabButton: "ViewDealOverview-tabButton",
    goToRentRollButton: "ViewDealOverview-goToRentRollButton",
};

export const tabs = [
    {
        value: "documents",
        label: "Documents",
    },
    {
        value: "operating-statement",
        label: "Operating Statement",
    },
] as const;

export type ViewDealOverviewParams = {
    useState: (params: { projectId: number; databaseId: number }) => {
        projectPropertyInfo: DealPropertyInfoWithId | null;
        projectIsPending: boolean;
        projectIsError: boolean;
    };
    useCases: () => {
        onMount: ({
            projectId,
            databaseId,
        }: {
            projectId: number;
            databaseId: number;
        }) => () => void;
        getProdaProjectPageUrl: (projectId: number) => string;
    };
    ViewExtractionAnnualized: ({
        projectId,
        databaseId,
        onViewExtraction,
    }: {
        projectId: number;
        databaseId: number;
        onViewExtraction: (params: {
            projectId: number;
            extractionId: number;
        }) => void;
        getExtractionReviewUrl: (params: {
            projectId: number;
            extractionId: number;
        }) => string;
    }) => React.ReactNode;
    ViewExtractionAnnualizedOptions: ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => React.ReactNode;
    ViewExtractionAnnualizedAdditionalOptions: ({
        projectId,
        databaseId,
    }: {
        projectId: number;
        databaseId: number;
    }) => React.ReactNode;
    ViewProjectDocuments?: ({
        projectId,
        databaseId,
        getExtractionUrl,
        getRentRollExtractionUrl,
        onViewExtraction,
        onViewRentRollExtraction,
    }: {
        projectId: number;
        databaseId: number;
        getExtractionUrl: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            extractionStatus: "draft" | "completed";
        }) => string;
        getRentRollExtractionUrl: (params: {
            fileId: number;
            extractionId: number;
            sheetId: string;
            databaseId: number;
        }) => string;
        onViewExtraction: (params: {
            projectId: number;
            databaseId: number;
            extractionId: number;
            extractionStatus: "draft" | "completed";
        }) => void;
        onViewRentRollExtraction: (params: {
            fileId: number;
            extractionId: number;
            sheetId: string;
            databaseId: number;
        }) => void;
    }) => React.ReactNode;
};

export type ViewDealOverviewProps = {
    projectId: number;
    databaseId: number;
    activeTab: TDealsOverviewTab;
    setActiveTab: (tabName: TDealsOverviewTab) => void;
    handleBackButtonClicked: () => void;
    handleAddFileButtonClicked: () => void;
    getExtractionUrl: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => string;
    getRentRollExtractionUrl: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => string;
    handleViewExtractionButtonClick: (params: {
        projectId: number;
        databaseId: number;
        extractionId: number;
        extractionStatus: "draft" | "completed";
    }) => void;
    handleViewRentRollExtractionButtonClick: (params: {
        fileId: number;
        extractionId: number;
        sheetId: string;
        databaseId: number;
    }) => void;
    handleDetailButtonClicked: (params: {
        projectId: number;
        databaseId: number;
    }) => void;
    handleActivityButtonClicked: (params: {
        projectId: number;
        databaseId: number;
    }) => void;
} & Omit<BoxProps, "children">;

export const createViewDealOverview = ({
    useState,
    useCases,
    ViewExtractionAnnualized,
    ViewExtractionAnnualizedOptions,
    ViewExtractionAnnualizedAdditionalOptions,
    ViewProjectDocuments: ViewProjectDocumentsInjected,
}: ViewDealOverviewParams) => {
    const ViewProjectDocuments = ViewProjectDocumentsInjected ?? (() => null);
    const ViewDealOverview = ({
        projectId,
        databaseId,
        activeTab,
        getExtractionUrl,
        getRentRollExtractionUrl,
        setActiveTab,
        handleBackButtonClicked,
        handleAddFileButtonClicked,
        handleViewExtractionButtonClick,
        handleViewRentRollExtractionButtonClick,
        handleDetailButtonClicked,
        handleActivityButtonClicked,
    }: ViewDealOverviewProps) => {
        const { projectPropertyInfo, projectIsPending, projectIsError } =
            useState({
                projectId,
                databaseId,
            });
        const { onMount, getProdaProjectPageUrl } = useCases();

        const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
        const dropdownTargetRef = React.useRef<HTMLInputElement>(null);

        useEffect(() => {
            let cleanup: (() => void) | undefined;

            if (projectId && databaseId) {
                cleanup = onMount({ projectId, databaseId });
            }

            return () => {
                cleanup?.();
            };
        }, [projectId, databaseId, onMount]);

        const isSmallScreen = useMediaQuery("(max-width: 1600px)");

        if (projectIsPending) {
            return (
                <LayoutFillParentCentered
                    sx={{
                        backgroundColor: "var(--proda-white)",
                    }}
                >
                    <Loader />
                </LayoutFillParentCentered>
            );
        }
        if (projectIsError || !projectPropertyInfo) {
            return (
                <MessageWithButton
                    variant="error"
                    message={ERROR_RETRIEVING_DEAL}
                    buttonLabel="Back"
                    onClick={handleBackButtonClicked}
                />
            );
        }

        const rentRollHref =
            projectPropertyInfo.projectId !== null &&
            projectPropertyInfo.projectId !== undefined
                ? getProdaProjectPageUrl(projectPropertyInfo.projectId)
                : "#";

        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <OverviewTopBar
                    deal={projectPropertyInfo}
                    handleAddFileButtonClicked={handleAddFileButtonClicked}
                    handleBackButtonClicked={handleBackButtonClicked}
                />

                <LayoutWithTopBar
                    displayVerticalDivider={
                        !isSmallScreen && activeTab === "operating-statement"
                    }
                    displayHorizontalDivider={
                        !isSmallScreen && activeTab === "operating-statement"
                    }
                    leftContent={
                        isSmallScreen ? (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    position: "relative",
                                }}
                            >
                                <Dropdown
                                    isShowing={isDropdownOpen}
                                    onClose={(): void => {
                                        setIsDropdownOpen(false);
                                    }}
                                    isDismissible
                                    onItemSelected={() => {
                                        setIsDropdownOpen(false);
                                    }}
                                    dropdownRenderElement={
                                        dropdownTargetRef.current
                                    }
                                >
                                    <Button
                                        leadingIcon={["far", "bars"]}
                                        isGhost
                                        size="medium"
                                        pattern="secondary"
                                        onClick={(): void =>
                                            setIsDropdownOpen(!isDropdownOpen)
                                        }
                                        isActive={isDropdownOpen}
                                    >
                                        {
                                            tabs.find(
                                                ({ value }) =>
                                                    value === activeTab,
                                            )?.label
                                        }
                                    </Button>
                                    <DropdownContent>
                                        <DropdownSelect
                                            labelledById="tabs"
                                            role="listbox"
                                            currentIndex={tabs.findIndex(
                                                ({ value }) =>
                                                    value === activeTab,
                                            )}
                                        >
                                            {tabs.map(({ value, label }) => (
                                                <DropdownSelectItem
                                                    key={value}
                                                    label={label}
                                                    role="option"
                                                    onClick={() => {
                                                        setActiveTab(value);
                                                        setIsDropdownOpen(
                                                            false,
                                                        );
                                                    }}
                                                    trailingIcon={
                                                        value === activeTab
                                                            ? "check"
                                                            : undefined
                                                    }
                                                />
                                            ))}
                                        </DropdownSelect>
                                    </DropdownContent>
                                </Dropdown>
                                <Box
                                    id="settings-dropdown-target"
                                    ref={dropdownTargetRef}
                                    sx={{
                                        minWidth: "180px",
                                        position: "absolute",
                                        top: "100%",
                                        left: 0,
                                    }}
                                />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 1,
                                }}
                            >
                                {tabs.map(({ value, label }) => (
                                    <Box
                                        key={value}
                                        component="button"
                                        onClick={() => {
                                            setActiveTab(value);
                                        }}
                                        sx={{
                                            padding: "2px 4px",
                                            border: "none",
                                            backgroundColor: "transparent",
                                            cursor: "pointer",
                                            color:
                                                activeTab === value
                                                    ? "var(--proda-red)"
                                                    : "var(--proda-neon-blue)",
                                            fontWeight: "bold",
                                            transition: "all 0.3s ease",
                                        }}
                                        data-testid={testIds.tabButton}
                                    >
                                        {label}
                                    </Box>
                                ))}
                            </Box>
                        )
                    }
                    rightContent={
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: "0.5rem",
                            }}
                        >
                            <Box>
                                {activeTab === "operating-statement" &&
                                    projectId && (
                                        <ViewExtractionAnnualizedOptions
                                            projectId={projectId}
                                            databaseId={databaseId}
                                        />
                                    )}
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: "0.5rem",
                                    alignItems: "center",
                                }}
                            >
                                <a
                                    href={rentRollHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Button
                                        leadingIcon={["far", "link"]}
                                        size="medium"
                                        pattern="primary"
                                        data-testid={testIds.goToRentRollButton}
                                        disabled={
                                            projectPropertyInfo.projectId ===
                                                null ||
                                            projectPropertyInfo.projectId ===
                                                undefined
                                        }
                                    >
                                        Go to Rent Roll
                                    </Button>
                                </a>
                                <VerticalDivider />
                                <Box sx={{ display: "flex", gap: "0.5rem" }}>
                                    <Box
                                        sx={{
                                            cursor: "pointer",
                                        }}
                                        onClick={() =>
                                            handleActivityButtonClicked({
                                                projectId,
                                                databaseId,
                                            })
                                        }
                                        data-testid={testIds.activityButton}
                                    >
                                        <IconBox>
                                            <Icon
                                                icon="clock-rotate-left"
                                                size="xl"
                                            />
                                        </IconBox>
                                    </Box>
                                    <Box
                                        component="span"
                                        className="fa-stack"
                                        sx={{
                                            fontSize: "0.7rem",
                                            cursor: "pointer",
                                        }}
                                        onClick={() => {
                                            handleDetailButtonClicked({
                                                projectId,
                                                databaseId,
                                            });
                                        }}
                                        data-testid={testIds.detailButton}
                                    >
                                        <Icon
                                            icon="folder"
                                            className="fa-stack-2x"
                                        />
                                        <Icon
                                            icon="gear"
                                            className="fa-stack-1x fa-inverse"
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    }
                    middleContent={
                        activeTab === "operating-statement" &&
                        projectId && (
                            <ViewExtractionAnnualizedAdditionalOptions
                                projectId={projectId}
                                databaseId={databaseId}
                            />
                        )
                    }
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {activeTab === "documents" && (
                            <ViewProjectDocuments
                                projectId={projectId}
                                databaseId={databaseId}
                                getExtractionUrl={getExtractionUrl}
                                getRentRollExtractionUrl={
                                    getRentRollExtractionUrl
                                }
                                onViewExtraction={
                                    handleViewExtractionButtonClick
                                }
                                onViewRentRollExtraction={
                                    handleViewRentRollExtractionButtonClick
                                }
                            />
                        )}
                        {activeTab === "operating-statement" && projectId && (
                            <ViewExtractionAnnualized
                                projectId={projectId}
                                databaseId={databaseId}
                                getExtractionReviewUrl={({
                                    projectId,
                                    extractionId,
                                }) => {
                                    return getExtractionUrl({
                                        projectId,
                                        databaseId,
                                        extractionId,
                                        extractionStatus: "completed",
                                    });
                                }}
                                onViewExtraction={({
                                    projectId,
                                    extractionId,
                                }) =>
                                    handleViewExtractionButtonClick({
                                        projectId,
                                        databaseId,
                                        extractionId,
                                        extractionStatus: "completed",
                                    })
                                }
                            />
                        )}
                    </Box>
                </LayoutWithTopBar>
            </Box>
        );
    };

    return ViewDealOverview;
};
